import type { ComputedRef, InjectionKey, Ref } from 'vue'
import type { RefreshToken, ResolvedRange, TimeRange } from '@/lib/charts/timeRange'
import { router, usePage } from '@inertiajs/vue3'
import { useDocumentVisibility, useIntervalFn } from '@vueuse/core'
import { computed, inject, provide, ref, watch } from 'vue'
import {
  AUTO_INTERVAL,
  autoInterval,
  DEFAULT_RANGE,
  DEFAULT_REFRESH,
  rangeParamKey,
  resolveRange,
  stepBackward as shiftBackward,
  stepForward as shiftForward,
  zoomOut as widenRange,
} from '@/lib/charts/timeRange'

const REFRESH_MS: Record<RefreshToken, number> = {
  'off': 0,
  '10s': 10_000,
  '30s': 30_000,
  '1m': 60_000,
  '5m': 300_000,
}

export interface UseTimeRangeOptions {
  /** Inertia prop name(s) this controller reloads when the range changes. */
  only: string[]
  /** Namespaces the URL/query keys so several charts can share one page. */
  namespace?: string
}

export interface TimeRangeController {
  range: Ref<TimeRange>
  /** `'auto'` or an explicit bucket token. */
  interval: Ref<string>
  refresh: Ref<RefreshToken>
  resolved: ComputedRef<ResolvedRange>
  /** The concrete bucket token sent to the backend. */
  effectiveInterval: ComputedRef<string>
  loading: Ref<boolean>
  setRange: (range: TimeRange) => void
  setInterval: (interval: string) => void
  setRefresh: (refresh: RefreshToken) => void
  stepBackward: () => void
  stepForward: () => void
  zoomOut: () => void
  reload: () => void
}

const TIME_RANGE_KEY: InjectionKey<TimeRangeController> = Symbol('time-range')

function currentParams(): URLSearchParams {
  return new URL(usePage().url, window.location.origin).searchParams
}

export function createTimeRange(options: UseTimeRangeOptions): TimeRangeController {
  const params = currentParams()
  const key = (name: string) => rangeParamKey(name, options.namespace)

  const range = ref<TimeRange>({
    from: params.get(key('from')) ?? DEFAULT_RANGE.from,
    to: params.get(key('to')) ?? DEFAULT_RANGE.to,
  })
  const interval = ref<string>(params.get(key('interval')) ?? AUTO_INTERVAL)
  const refresh = ref<RefreshToken>((params.get(key('refresh')) as RefreshToken) ?? DEFAULT_REFRESH)
  const loading = ref(false)

  const resolved = computed(() => resolveRange(range.value))
  const effectiveInterval = computed(() =>
    interval.value === AUTO_INTERVAL ? autoInterval(range.value) : interval.value,
  )

  function writeUrl() {
    const url = new URL(window.location.href)
    url.searchParams.set(key('from'), range.value.from)
    url.searchParams.set(key('to'), range.value.to)

    if (interval.value === AUTO_INTERVAL)
      url.searchParams.delete(key('interval'))
    else
      url.searchParams.set(key('interval'), interval.value)

    if (refresh.value === DEFAULT_REFRESH)
      url.searchParams.delete(key('refresh'))
    else
      url.searchParams.set(key('refresh'), refresh.value)

    window.history.replaceState(window.history.state, '', url)
  }

  function reload() {
    if (options.only.length === 0)
      return

    const data: Record<string, string> = {
      [key('from')]: range.value.from,
      [key('to')]: range.value.to,
    }
    if (interval.value !== AUTO_INTERVAL)
      data[key('interval')] = interval.value

    router.reload({
      only: options.only,
      data,
      onStart: () => (loading.value = true),
      onFinish: () => (loading.value = false),
    })
  }

  watch([range, interval], () => {
    writeUrl()
    reload()
  })

  const visibility = useDocumentVisibility()
  const intervalMs = computed(() => REFRESH_MS[refresh.value] || 60_000)
  const { pause, resume } = useIntervalFn(reload, intervalMs, { immediate: false })

  watch([refresh, visibility], () => {
    if (refresh.value !== 'off' && visibility.value !== 'hidden')
      resume()
    else
      pause()
  }, { immediate: true })

  function setRange(next: TimeRange) {
    range.value = next
  }

  return {
    range,
    interval,
    refresh,
    resolved,
    effectiveInterval,
    loading,
    setRange,
    setInterval: (next: string) => (interval.value = next),
    setRefresh: (next: RefreshToken) => {
      refresh.value = next
      writeUrl()
    },
    stepBackward: () => setRange(shiftBackward(range.value)),
    stepForward: () => setRange(shiftForward(range.value)),
    zoomOut: () => setRange(widenRange(range.value)),
    reload,
  }
}

/** Create a controller and share it with descendant toolbars/panels (page scope). */
export function provideTimeRange(options: UseTimeRangeOptions): TimeRangeController {
  const controller = createTimeRange(options)
  provide(TIME_RANGE_KEY, controller)
  return controller
}

/** Read the page-scoped controller provided by an ancestor. */
export function injectTimeRange(): TimeRangeController {
  const controller = injectTimeRangeOptional()
  if (!controller)
    throw new Error('injectTimeRange() requires a provideTimeRange() ancestor')
  return controller
}

/**
 * Like `injectTimeRange`, but returns `null` when no ancestor provided one —
 * lets a chart wire drag-to-zoom to the page range when present, yet still work
 * standalone (e.g. given an explicit controller or a static range).
 */
export function injectTimeRangeOptional(): TimeRangeController | null {
  return inject(TIME_RANGE_KEY, null)
}
